import { clamp } from "@/lib/utils";
import { Table } from "@tanstack/react-table";
import { PreviewState } from "./data-table-preview";
import React, { KeyboardEvent } from "react";
import { useTableEvents } from "./data-table-context";

const overlapHorizontal = (a: DOMRect, b: DOMRect) =>
  a.right > b.left && a.left < b.right;

export const findElement = (
  el: HTMLElement,
  top: boolean,
): [number, HTMLElement] | undefined => {
  const originalRect = el.getBoundingClientRect();
  const next = (el: Element) =>
    top ? el.previousElementSibling : el.nextElementSibling;
  let current = next(el);
  let index = 0;
  while (current) {
    index += 1;
    if (overlapHorizontal(originalRect, current.getBoundingClientRect())) {
      return [index, current as HTMLElement];
    }
    current = next(current);
  }
};

export type TableKeyboardHandlersOptions = {
  disableUpDown?: boolean;
  arrowsOnKeyUp?: boolean;
};

export const useTableKeyboardHandlers = (
  table: Table<any>,
  ref?: React.MutableRefObject<HTMLDivElement | null>,
  options?: TableKeyboardHandlersOptions,
  // parentRef: React.MutableRefObject<HTMLDivElement | null>,
  // childRef: React.MutableRefObject<HTMLDivElement | null>,
) => {
  const events = useTableEvents();
  return React.useMemo(
    () => ({
      onKeyDown: (ev: KeyboardEvent) => {
        if (ev.defaultPrevented) return;
        const target = ev.target as any;
        if (
          ["input", "textarea"].includes(
            (target?.tagName ?? "").toLowerCase(),
          ) ||
          target.isContentEditable ||
          ["textbox"].includes(target?.getAttribute("role") ?? "")
        ) {
          return;
        }
        if (
          !options?.arrowsOnKeyUp &&
          (ev.key == "ArrowLeft" ||
            (!options?.disableUpDown && ev.key == "ArrowUp"))
        ) {
          let index = 1;
          if (ev.key == "ArrowUp" && table.isGalleryEnabled() && ref?.current) {
            const [_index, el] = findElement(ref?.current, true) ?? [];
            index = _index ?? index;
            // if (el) {
            //   console.log("above", el, index);
            //   el.click();
            //   return;
            // }
          }
          table.setPositionIndex(
            (p) => (p === undefined ? p : clamp(p - index, 0)),
            true,
          );
        } else if (
          !options?.arrowsOnKeyUp &&
          (ev.key == "ArrowRight" ||
            (!options?.disableUpDown && ev.key == "ArrowDown"))
        ) {
          let index = 1;
          if (
            ev.key == "ArrowDown" &&
            table.isGalleryEnabled() &&
            ref?.current
          ) {
            const [_index, el] = findElement(ref?.current, false) ?? [];
            index = _index ?? index;
            // console.log("below", el, index);
            // if (el) {
            //   el.click();
            //   return;
            // }
          }
          table.setPositionIndex(
            (p) =>
              p === undefined
                ? p
                : clamp(p + index, undefined, table.getRowCount() - 1),
            true,
          );
        } else if (ev.key == "Escape") {
          // parentRef.current?.focus();
          table.setPreview((p) =>
            p === undefined
              ? p
              : table.isSkipPreview()
                ? 0
                : (clamp(p - 1, 0, 2) as PreviewState),
          );
        } else if (ev.key == "Enter") {
          // childRef.current?.focus();
          table.setPreview((p) =>
            p === undefined
              ? p
              : table.isSkipPreview()
                ? 2
                : (clamp(p + 1, 0, 2) as PreviewState),
          );
        } else {
          events.emit("keyDown", ev);
          return;
        }
        ev.stopPropagation();
      },
      onKeyUp: (ev: KeyboardEvent) => {
        if (ev.defaultPrevented) return;
        const target = ev.target as any;
        if (
          ["input", "textarea"].includes(
            (target?.tagName ?? "").toLowerCase(),
          ) ||
          target.isContentEditable ||
          ["textbox"].includes(target?.getAttribute("role") ?? "")
        ) {
          return;
        }
        if (ev.key == "Space") {
          table.getPositionRow()?.toggleSelected();
        }
        if (
          options?.arrowsOnKeyUp &&
          (ev.key == "ArrowLeft" ||
            (!options?.disableUpDown && ev.key == "ArrowUp"))
        ) {
          let index = 1;
          if (ev.key == "ArrowUp" && table.isGalleryEnabled() && ref?.current) {
            const [_index, el] = findElement(ref?.current, true) ?? [];
            index = _index ?? index;
            // if (el) {
            //   console.log("above", el, index);
            //   el.click();
            //   return;
            // }
          }
          table.setPositionIndex(
            (p) => (p === undefined ? p : clamp(p - index, 0)),
            true,
          );
        } else if (
          options?.arrowsOnKeyUp &&
          (ev.key == "ArrowRight" ||
            (!options?.disableUpDown && ev.key == "ArrowDown"))
        ) {
          let index = 1;
          if (
            ev.key == "ArrowDown" &&
            table.isGalleryEnabled() &&
            ref?.current
          ) {
            const [_index, el] = findElement(ref?.current, false) ?? [];
            index = _index ?? index;
            // console.log("below", el, index);
            // if (el) {
            //   el.click();
            //   return;
            // }
          }
          table.setPositionIndex(
            (p) =>
              p === undefined
                ? p
                : clamp(p + index, undefined, table.getRowCount() - 1),
            true,
          );
        } else {
          events.emit("keyUp", ev);
          return;
        }
        ev.stopPropagation();
      },
    }),
    // [table, parentRef, childRef],
    [table, ref, options?.disableUpDown, options?.arrowsOnKeyUp, events],
  );
};
